"use client";

import PageAnchor from '@/components/page-anchor';
import styles from './_reduced-gc.module.scss';
import CustomImage from '@/components/image';

const ContentWrapper = ({ isMobile = false }) => {
  return (
    <div className={`${styles.content_section} ${isMobile ? styles.content_section_mobile : styles.content_section_desktop}`}>
      <p className={styles.content_section__text}>Achieved &gt;2.5 mg/m<sup>2</sup>/day reduction or a GC dose in the physiologic range while maintaining or improving
        androstenedione levels<sup>9†</sup></p>
      <div className={styles.content_section__image}>
        <CustomImage
          src="/images/page/hcp/clinical-information/Clinical-Information_Pediatric-Physiologic-Daily-GC-Dose-Reduction.svg"
          width={566}
          height={416}
          priority
          alt="57% of CRENESSITY patients achieved a >2.5 mg/m²/day reduction or a GC dose in the physiologic range vs 3% with placebo."
        />
      </div>
    </div>
  )}

export default function HcpClinicalInformationReducedGc() {
  return (
    <>
      <PageAnchor anchorId="trial-1-efficacy-reduced-gc" instant />
      <section className={styles.container}>
        <div className="container">
          <h2>More patients reduced GCs while maintaining <br/> or improving androstenedione levels</h2>
          <div className={styles.content_wrapper}>
            <div className={styles.content_section}>
              <p className={styles.content_section__text}>Achieved physiologic daily GC dose while maintaining or improving androstenedione levels<sup>9</sup><span className="super-asterisc">*</span><sup>†</sup>
              </p>
              <div className={styles.content_section__image}>
                <CustomImage
                  src="/images/page/hcp/clinical-information/Clinical-Information_Pediatric-Physiologic-Daily-GC-Dose.svg"
                  width={566}
                  height={416}
                  priority
                  alt="30% of patients taking CRENESSITY achieved a daily GC dose in the physiologic range vs 0% with placebo."
                />
              </div>
            </div>

            <ContentWrapper />
          </div>
          <div className={styles.callout}>The percentage of children on CRENESSITY who achieved a physiologic GC dose while maintaining or improving androstenedione levels remained stable for 1 year.<sup>3</sup></div>
          <div className={styles.largeText}>
            <p>At week 52, the percentage of children on CRENESSITY who achieved a physiologic daily GC dose while maintaining or improving androstenedione levels was 32% (21/65).<sup>3</sup></p>
          </div>
          <ContentWrapper isMobile={true} />
          <div className={styles.purpleText}>
            <p>These endpoints were not adjusted for multiplicity; these descriptive results could represent chance findings.<sup>8</sup></p>
          </div>
          <div className={styles.footnotes}>
            <ul className="footnotes">
              <li><span className="hang">*</span><span>In the clinical trial, the physiologic range was defined as ≤11 mg/m<sup>2</sup>/day in hydrocortisone equivalents. Maintaining or improving androstenedione level was defined as ≤120% of the baseline value or ≤ULN for sex and either age (for Tanner stage 1) or pubertal stage (for Tanner stages 2-5) for the visit. Patients who were unable to maintain or improve androstenedione levels with a GC dose reduction were considered to have a 0% change in daily GC dose.<sup>1,9</sup></span>
              </li>
              <li><sup className="hang">†</sup><span>Mean baseline GC doses in hydrocortisone equivalents were 16.4 ± 3.9 mg/m<sup>2</sup>/day for all participants, 16.3 ± 3.4 mg/m<sup>2</sup>/day for placebo, and 16.5 ± 4.2 mg/m<sup>2</sup>/day for CRENESSITY.<sup>9</sup></span>
              </li>
            </ul>
          </div>
          <div className="hcp-callout">
            A hydrocortisone dose of 11 mg/m<sup>2</sup>/day is roughly equivalent to<br className="hidden tablet-down:inline-block"/> 10 mg/day for a 6-year-old, 14 mg/day for a 10-year-old, and 16 mg/day for a 12-year-old.
          </div>
        </div>
      </section>
    </>
  );
}
